// teams.config.ts
import { environment } from 'src/environments/environment';

export interface TeamsConfiguration {
    // UI Configuration
    ui: {
      enableSearch: boolean;
      enableSort: boolean;
      enableMobileOptimizations: boolean;
      showCampaignBanner: boolean;
      enableAnimations: boolean;
      cardHoverEffects: boolean;
      stickyCartFooter: boolean;
    };
  
    // Pagination and Limits
    pagination: {
      teamsPerPage: number;
      maxTeamsPerUser: number;
      loadMoreIncrement: number;
      enableInfiniteScroll: boolean;
    };
  
    // Performance Settings
    performance: {
      enableVirtualScrolling: boolean;
      imageLazyLoading: boolean;
      debounceSearchMs: number;
      cacheTimeoutMs: number;
      retryAttempts: number;
      retryDelayMs: number;
    };
  
    // Business Rules
    business: {
      defaultTeamCost: number;
      maxFreeTeams: number;
      requirePremiumForMultipleTeams: boolean;
      allowTeamDowngrades: boolean;
      enableBulkOperations: boolean;
    };
  
    // Analytics
    analytics: {
      trackTeamViews: boolean;
      trackCartActions: boolean;
      trackConversions: boolean;
      enableHeatmaps: boolean;
    };
  
    // Accessibility
    accessibility: {
      enableHighContrast: boolean;
      enableReducedMotion: boolean;
      keyboardNavigation: boolean;
      screenReaderOptimizations: boolean;
    };
  }

// Create a deep partial type for configuration overrides
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

// Default configuration
export const DEFAULT_TEAMS_CONFIG: TeamsConfiguration = {
  ui: {
    enableSearch: true,
    enableSort: true,
    enableMobileOptimizations: true,
    showCampaignBanner: true,
    enableAnimations: true,
    cardHoverEffects: true,
    stickyCartFooter: true
  },

  pagination: {
    teamsPerPage: 20,
    maxTeamsPerUser: 50,
    loadMoreIncrement: 10,
    enableInfiniteScroll: false
  },

  performance: {
    enableVirtualScrolling: false,
    imageLazyLoading: true,
    debounceSearchMs: 300,
    cacheTimeoutMs: 300000, // 5 minutes
    retryAttempts: 3,
    retryDelayMs: 1000
  },

  business: {
    defaultTeamCost: 5,
    maxFreeTeams: 1,
    requirePremiumForMultipleTeams: true,
    allowTeamDowngrades: true,
    enableBulkOperations: false
  },

  analytics: {
    trackTeamViews: true,
    trackCartActions: true,
    trackConversions: true,
    enableHeatmaps: false
  },

  accessibility: {
    enableHighContrast: true,
    enableReducedMotion: true,
    keyboardNavigation: true,
    screenReaderOptimizations: true
  }
};

// Export configuration factory with proper deep partial typing
export function createTeamsConfig(overrides: DeepPartial<TeamsConfiguration> = {}): TeamsConfiguration {
  return {
    ui: { 
      ...DEFAULT_TEAMS_CONFIG.ui, 
      ...(overrides.ui || {}) 
    },
    pagination: { 
      ...DEFAULT_TEAMS_CONFIG.pagination, 
      ...(overrides.pagination || {}) 
    },
    performance: { 
      ...DEFAULT_TEAMS_CONFIG.performance, 
      ...(overrides.performance || {}) 
    },
    business: { 
      ...DEFAULT_TEAMS_CONFIG.business, 
      ...(overrides.business || {}) 
    },
    analytics: { 
      ...DEFAULT_TEAMS_CONFIG.analytics, 
      ...(overrides.analytics || {}) 
    },
    accessibility: { 
      ...DEFAULT_TEAMS_CONFIG.accessibility, 
      ...(overrides.accessibility || {}) 
    }
  };
}